// src/features/dashboard/hooks/Manager/IdleTimeReport/useIdleTimeReport.ts

import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  useManagerDate,
} from '../../../context/ManagerDateContext';

import {
  formatApiDate,
} from '../../../utils/date.utils';

import {
  getIdleTimeKpi,
  getIdleTeamChart,
  getIdleBreakdown,
  getIdleTrend,
  getAssociateIdleDetails,
} from '../../../services/Manager/idleTimeReport.service';

import type {
  IdleTimeKpiResponse,
  IdleTeamChartResponse,
  IdleBreakdownResponse,
  IdleTrendResponse,
  AssociateIdleDetail,
  IdleTimeRequest,
  IdleTimeKpiRequest,
} from '../../../types/Manager/idleTime.types';

import {
  getManagerEmployeeCode,
} from '../../../../../features/auth/utils/Login/authStorage';


// ============================================================
// CONSTANT
// ============================================================

const IDLE_THRESHOLD = 2;


// ============================================================
// HOOK
// ============================================================

const useIdleTimeReport = () => {
  const {
    fromDate,
    toDate,
  } = useManagerDate();

  const employeeCode =
    getManagerEmployeeCode();


  // ============================================================
  // DATA
  // ============================================================

  const [
    kpi,
    setKpi,
  ] = useState<IdleTimeKpiResponse | null>(
    null,
  );

  const [
    teamChart,
    setTeamChart,
  ] = useState<IdleTeamChartResponse[]>(
    [],
  );

  const [
    breakdown,
    setBreakdown,
  ] = useState<IdleBreakdownResponse[]>(
    [],
  );

  const [
    idleTrend,
    setIdleTrend,
  ] = useState<IdleTrendResponse[]>(
    [],
  );

  const [
    associateDetails,
    setAssociateDetails,
  ] = useState<AssociateIdleDetail[]>(
    [],
  );


  // ============================================================
  // LOADING
  // ============================================================

  const [
    kpiLoading,
    setKpiLoading,
  ] = useState(false);

  const [
    teamChartLoading,
    setTeamChartLoading,
  ] = useState(false);

  const [
    breakdownLoading,
    setBreakdownLoading,
  ] = useState(false);

  const [
    trendLoading,
    setTrendLoading,
  ] = useState(false);

  const [
    associateLoading,
    setAssociateLoading,
  ] = useState(false);


  // ============================================================
  // ERRORS
  // ============================================================

  const [
    kpiError,
    setKpiError,
  ] = useState<string | null>(null);

  const [
    teamChartError,
    setTeamChartError,
  ] = useState<string | null>(null);

  const [
    breakdownError,
    setBreakdownError,
  ] = useState<string | null>(null);

  const [
    trendError,
    setTrendError,
  ] = useState<string | null>(null);

  const [
    associateError,
    setAssociateError,
  ] = useState<string | null>(null);


  // ============================================================
  // LOAD DATA
  // ============================================================

  const loadData = useCallback(
    async () => {
      if (
        !employeeCode ||
        !fromDate ||
        !toDate
      ) {
        console.warn(
          'Idle Time Report - missing request values',
          {
            employeeCode,
            fromDate,
            toDate,
          },
        );

        return;
      }


      // ========================================================
      // REQUEST
      // ========================================================

      const request: IdleTimeRequest = {
        employeeCode:
          String(
            employeeCode,
          ).trim(),

        fromDate:
          formatApiDate(
            fromDate,
          ),

        toDate:
          formatApiDate(
            toDate,
          ),
      };

      const kpiRequest:
        IdleTimeKpiRequest = {
        ...request,
        idleThreshold:
          IDLE_THRESHOLD,
      };


      console.log(
        'IDLE TIME REPORT REQUEST:',
        request,
      );


      // ========================================================
      // START LOADING
      // ========================================================

      setKpiLoading(true);
      setTeamChartLoading(true);
      setBreakdownLoading(true);
      setTrendLoading(true);
      setAssociateLoading(true);

      setKpiError(null);
      setTeamChartError(null);
      setBreakdownError(null);
      setTrendError(null);
      setAssociateError(null);


      // ========================================================
      // KPI
      // ========================================================

      const kpiPromise =
        getIdleTimeKpi(
          kpiRequest,
        )
          .then((response) => {
            console.log(
              'Idle KPI:',
              response,
            );

            setKpi(response);
          })
          .catch((error) => {
            console.error(
              'Idle KPI Error:',
              error,
            );

            setKpi(null);

            setKpiError(
              'Failed to load KPI Cards',
            );
          })
          .finally(() => {
            setKpiLoading(false);
          });


      // ========================================================
      // TEAM
      // ========================================================

      const teamPromise =
        getIdleTeamChart(
          request,
        )
          .then((response) => {
            const result =
              Array.isArray(response)
                ? response
                : [];

            console.log(
              'Idle Team:',
              result,
            );

            setTeamChart(result);
          })
          .catch((error) => {
            console.error(
              'Idle Team Error:',
              error,
            );

            setTeamChart([]);

            setTeamChartError(
              'Failed to load Team Chart',
            );
          })
          .finally(() => {
            setTeamChartLoading(false);
          });


      // ========================================================
      // BREAKDOWN
      // ========================================================

      const breakdownPromise =
        getIdleBreakdown(
          request,
        )
          .then((response) => {
            const result =
              Array.isArray(response)
                ? response
                : [];

            console.log(
              'Idle Breakdown:',
              result,
            );

            setBreakdown(result);
          })
          .catch((error) => {
            console.error(
              'Idle Breakdown Error:',
              error,
            );

            setBreakdown([]);

            setBreakdownError(
              'Failed to load Breakdown',
            );
          })
          .finally(() => {
            setBreakdownLoading(false);
          });


      // ========================================================
      // DAILY TREND
      // ========================================================

      const trendPromise =
        getIdleTrend(
          request,
        )
          .then((response) => {
            const result =
              Array.isArray(response)
                ? response
                : [];

            console.log(
              'Idle Trend:',
              result,
            );

            setIdleTrend(result);
          })
          .catch((error) => {
            console.error(
              'Idle Trend Error:',
              error,
            );

            setIdleTrend([]);

            setTrendError(
              'Failed to load Idle Trend',
            );
          })
          .finally(() => {
            setTrendLoading(false);
          });


      // ========================================================
      // ASSOCIATE DETAILS
      // ========================================================

      const associatePromise =
        getAssociateIdleDetails(
          request,
        )
          .then((response) => {
            const result =
              Array.isArray(response)
                ? response
                : [];

            console.log(
              '====================================',
            );

            console.log(
              'ASSOCIATE DETAILS API RESULT:',
              result,
            );

            console.log(
              'ASSOCIATE DETAILS COUNT:',
              result.length,
            );

            console.log(
              'FIRST ASSOCIATE:',
              result[0],
            );

            console.log(
              '====================================',
            );

            setAssociateDetails(
              result,
            );
          })
          .catch((error) => {
            console.error(
              'Associate Details Error:',
              error,
            );

            setAssociateDetails([]);

            setAssociateError(
              'Failed to load Associate Details',
            );
          })
          .finally(() => {
            setAssociateLoading(false);
          });


      // ========================================================
      // WAIT FOR ALL
      // ========================================================

      await Promise.allSettled([
        kpiPromise,
        teamPromise,
        breakdownPromise,
        trendPromise,
        associatePromise,
      ]);
    },
    [
      employeeCode,
      fromDate,
      toDate,
    ],
  );


  // ============================================================
  // EFFECT
  // ============================================================

  useEffect(() => {
    void loadData();
  }, [loadData]);


  // ============================================================
  // RETURN
  // ============================================================

  return {
    kpi,
    teamChart,
    breakdown,
    idleTrend,
    associateDetails,

    kpiLoading,
    teamChartLoading,
    breakdownLoading,
    trendLoading,
    associateLoading,

    kpiError,
    teamChartError,
    breakdownError,
    trendError,
    associateError,

    refresh: loadData,
  };
};


export default useIdleTimeReport;